import { Spinner } from "@/components/UI/Spinner";
import { RunStatus } from "@/generated";
import { Badge } from "@/components/shadcn/badge";
import {
  canAwaitHumanFeedback,
  isSubflowNodeType,
  isTaskNodeType,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ExpandableNode/nodeUiStatus";

import {
  Banknote,
  Check,
  Clock,
  Info,
  Pause,
  PersonStanding,
  Redo,
  Shield,
  X,
} from "lucide-react";

export const StatusIcon = ({
  nodeStatus,
  runStatus,
  errorHandlerId,
  nodeType,
  isRetrying,
}: {
  nodeStatus: string;
  runStatus: RunStatus;
  errorHandlerId?: string | undefined;
  nodeType?: string;
  isRetrying?: boolean;
}) => {
  if (isRetrying) {
    return <Spinner className="h-3 w-4 text-primary" />;
  }
  if (nodeStatus === "succeeded") {
    return <Check size="1rem" color="#32D583" strokeWidth="1.25px" />;
  } else if (
    errorHandlerId &&
    nodeStatus === "errored" &&
    runStatus === "running"
  ) {
    return (
      <>
        <Spinner className="h-3 w-4 mr-2 text-primary" />
        <Shield color="#F97066" size="1rem" strokeWidth="1.25px" />
      </>
    );
  } else if (nodeStatus === "payment_required") {
    return <Banknote size="1rem" color="var(--primary)" strokeWidth="1.25px" />;
  } else if (
    nodeStatus === "paused" ||
    (nodeStatus === "running" && runStatus === "stopped") ||
    (nodeStatus === "awaiting_human_feedback" &&
      !canAwaitHumanFeedback(nodeType))
  ) {
    return (
      <Badge variant="outline" isCompact>
        Paused
      </Badge>
    );
  } else if (
    nodeStatus === "running" &&
    runStatus === "awaiting_human_feedback" &&
    canAwaitHumanFeedback(nodeType)
  ) {
    if (isTaskNodeType(nodeType) || isSubflowNodeType(nodeType)) {
      return (
        <Clock size="1.125rem" color="var(--primary)" strokeWidth="1.5px" />
      );
    }
    return (
      <PersonStanding
        size="1.125rem"
        color="var(--primary)"
        strokeWidth="1.5px"
      />
    );
  } else if (nodeStatus === "running" && runStatus === "failed") {
    return <Pause size="1rem" color="var(--primary)" strokeWidth="1.25px" />;
  } else if (nodeStatus === "awaiting_human_feedback") {
    if (isTaskNodeType(nodeType) || isSubflowNodeType(nodeType)) {
      return (
        <Clock size="1.125rem" color="var(--primary)" strokeWidth="1.5px" />
      );
    }
    return (
      <PersonStanding
        size="1.125rem"
        color="var(--primary)"
        strokeWidth="1.5px"
      />
    );
  } else if (
    nodeStatus === "errored" &&
    runStatus === "awaiting_human_feedback" &&
    (nodeType === "CreateTaskNode" ||
      nodeType === "HumanInTheLoopNode" ||
      nodeType === "InvokeSubflowNode")
  ) {
    if (nodeType === "CreateTaskNode" || nodeType === "InvokeSubflowNode") {
      return (
        <Clock size="1.125rem" color="var(--primary)" strokeWidth="1.5px" />
      );
    }
    return (
      <PersonStanding
        size="1.125rem"
        color="var(--primary)"
        strokeWidth="1.5px"
      />
    );
  } else if (nodeStatus === "errored") {
    return <X size="1rem" color="#F97066" strokeWidth="1.25px" />;
  } else if (nodeStatus === "warning") {
    return <Info size="1rem" color="#FDB022" strokeWidth="1.25px" />;
  } else if (nodeStatus === "running") {
    return <Spinner className="h-3 w-4 text-primary" />;
  } else if (nodeStatus === "skipped") {
    return <Redo size="1rem" color="var(--primary)" strokeWidth="1.25px" />;
  }
  return <></>;
};
